import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Tabs,
  Tab,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Stack,
  Divider,
  Grid,
  CardActionArea,
  Avatar,
} from '@mui/material';
import { Add, ExpandMore, Delete, MoreVert } from '@mui/icons-material';
import { fetchProducts, deleteProduct } from '../api/superAdmin';
import { useNavigate } from 'react-router-dom';
import { saveAs } from 'file-saver';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

interface Location {
  name: string;
  stock_type: 'in_store' | 'online';
  stock: number;
}

interface Variant {
  id: number;
  sku: string;
  price: number;
  locations: Location[];
  inStoreStock: number;
  onlineStock: number;
}


interface Product {
  id: number;
  name: string;
  sku: string;
  description: string;
  price: string;
  supplier_price: number;
  image_url: string | null;
  status: string;
  inventoryDetails: { totalStock: number; variantsCount: number } | null;
  categories: string[];
  variants: Variant[];
}

const ProductListPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedProduct, setSelectedProduct] = useState<number | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const data = await fetchProducts();
      const formattedData = data.map((item: any) => {
  const variants = item.variants || [];

  const prices = variants.map((v: any) => v.price).filter(Boolean);
  const priceRange =
    prices.length > 1
      ? `${Math.min(...prices).toFixed(2)} - ${Math.max(...prices).toFixed(2)}`
      : `${prices[0]?.toFixed(2) || '0.00'}`;

  const formattedVariants = variants.map((variant: any) => {
    const locations = Array.isArray(variant.locations) ? variant.locations : [];

    const locationData = locations.map((loc: any) => ({
      name: loc.location_name || 'Unknown',
      stock_type: loc.stock_type || 'in_store',
      stock: loc.stock || 0,
    }));

    const inStoreStock = locationData
  .filter((l: Location) => l.stock_type === 'in_store')
  .reduce((sum: number, l: Location) => sum + l.stock, 0);

const onlineStock = locationData
  .filter((l: Location) => l.stock_type === 'online')
  .reduce((sum: number, l: Location) => sum + l.stock, 0);



    return {
      id: variant.id,
      sku: variant.sku,
      price: variant.price,
      locations: locationData,
      inStoreStock,
      onlineStock,
    };
  });

const totalStock = formattedVariants.reduce(
  (sum: number, v: Variant) => sum + v.inStoreStock + v.onlineStock,
  0
);

  return {
    id: item.product_id,
    name: item.product_name,
    description: item.description,
    price: priceRange,
    supplier_price: item.supplier_price,
    sku: formattedVariants[0]?.sku || 'N/A',
    image_url: item.image_url || 'https://fakeimg.pl/600x400?text=FC',
    status: 'Active',
    inventoryDetails: { totalStock, variantsCount: formattedVariants.length },
    categories: item.category ? [item.category] : ['Uncategorized'],
    variants: formattedVariants,
  };
});

      setProducts(formattedData);
    } catch (err) {
      console.error('Failed to fetch products:', err);
      setProducts([]);
    }
  };

  const handleDeleteProduct = async (id: number) => {
    try {
      await deleteProduct(id);
      alert('Product deleted successfully');
      loadProducts();
    } catch (err) {
      console.error('Failed to delete product:', err);
      alert('Failed to delete product');
    }
  };

  const handleDeleteSelected = async () => {
    try {
      await Promise.all(selectedProducts.map((id) => deleteProduct(id)));
      alert('Selected products deleted successfully');
      setSelectedProducts([]);
      loadProducts();
    } catch (err) {
      console.error('Failed to delete selected products:', err);
      alert('Failed to delete selected products');
    }
  };

  const handleSelectProduct = (id: number) => {
    setSelectedProducts((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map((product) => product.id));
    }
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, productId: number) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedProduct(productId);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedProduct(null);
  };

  const handleExportCSV = () => {
    const csvData = products.map((product) => ({
      ID: product.id,
      Name: product.name,
      SKU: product.sku,
      Description: product.description,
      Price: product.price,
      SupplierPrice: product.supplier_price,
      Category: product.categories.join(', '),
      Status: product.status,
      Inventory: product.inventoryDetails ? product.inventoryDetails.totalStock : 'Not tracked',
    }));

    const worksheet = XLSX.utils.json_to_sheet(csvData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Products');
    const excelBuffer = XLSX.write(workbook, { bookType: 'csv', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/csv' });
    saveAs(data, 'products_export.csv');
  };

  const handleImportCSV = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = e.target?.result;
        if (data) {
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const json = XLSX.utils.sheet_to_json(worksheet);
          console.log(json);
        }
      };
      reader.readAsBinaryString(file);
    }
  };

  const handleExportPDF = () => {
    const stripHtml = (html: string) => {
      const tmp = document.createElement('DIV');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    };
  
    const doc = new jsPDF();
  
    doc.setFontSize(14);
    doc.text('Product Inventory Report', 14, 15);
  
    const columns = [
      'Product Name',
      'Description',
      'Supplier Price',
      'Retail Price',
      'Markup (%)',
      'Margin (%)',
      'Variants',
    ];
  
    const data = products.map((product) => {
      if (!product.variants || !Array.isArray(product.variants)) return [];
  
      const variants = product.variants
        .map((variant) => `${variant.sku} - $${variant.price.toFixed(2)}`)
        .join('\n');
  
      const supplierPrice = parseFloat(product.supplier_price.toString()) || 0;
      const retailPrice = parseFloat(product.price.split(' - ')[0]) || 0;
  
      const markup = ((retailPrice - supplierPrice) / supplierPrice) * 100;
      const margin = ((retailPrice - supplierPrice) / retailPrice) * 100;
  
      return [
        product.name,
        product.description ? stripHtml(product.description) : '',
        `$${supplierPrice.toFixed(2)}`,
        `$${retailPrice.toFixed(2)}`,
        `${isNaN(markup) ? '0.00' : markup.toFixed(2)}%`,
        `${isNaN(margin) ? '0.00' : margin.toFixed(2)}%`,
        variants,
      ];
    }).filter(row => row.length > 0);
  
    autoTable(doc, {
      head: [columns],
      body: data,
      startY: 20,
      styles: {
        overflow: 'linebreak',
        cellPadding: 3,
        fontSize: 8,
      },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { cellWidth: 45 },
        2: { cellWidth: 20 },
        3: { cellWidth: 20 },
        4: { cellWidth: 20 },
        5: { cellWidth: 20 },
        6: { cellWidth: 60 },
      },
      didDrawPage: (data) => {
        const pageNumber = doc.getCurrentPageInfo().pageNumber;
        const totalPages = doc.getNumberOfPages();
        const pageSize = doc.internal.pageSize;
        const pageHeight = pageSize.height || pageSize.getHeight();
  
        doc.setFontSize(8);
        doc.text(
          `Page ${pageNumber} of ${totalPages}`,
          data.settings.margin.left,
          pageHeight - 10
        );
      }
    });
  
    doc.save('product_inventory_report.pdf');
  };

  const filteredProducts = products
    .filter((product) =>
      selectedStatus === 'All' ? true : product.status === selectedStatus
    )
    .filter((product) =>
      searchQuery
        ? product.name.toLowerCase().includes(searchQuery.toLowerCase())
        : true
    );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Products
      </Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 2 : 0,
        }}
      >
        <Tabs
          value={selectedStatus}
          onChange={(e, newValue) => setSelectedStatus(newValue)}
          textColor="primary"
          indicatorColor="primary"
          variant={isMobile ? 'scrollable' : 'standard'}
        >
          <Tab label="All" value="All" />
          <Tab label="Active" value="Active" />
          <Tab label="Draft" value="Draft" />
          <Tab label="Archived" value="Archived" />
        </Tabs>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search products"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/products/add')}
          >
            Add Product
          </Button>
          {selectedProducts.length > 0 && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Delete />}
              onClick={handleDeleteSelected}
            >
              Delete Selected
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleExportCSV}
          >
            Export CSV
          </Button>
          <Button
            variant="contained"
            color="primary"
            component="label"
          >
            Import CSV
            <input
              type="file"
              hidden
              onChange={handleImportCSV}
            />
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExportPDF}
          >
            Export PDF
          </Button>
        </Box>
      </Box>

      {isMobile ? (
        // Mobile Card View
        <Grid container spacing={2}>
          {filteredProducts.map((product) => (
            <Grid item xs={12} key={product.id}>
              <Card>
                <CardActionArea onClick={() => navigate(`/products/edit/${product.id}`)}>
                  <Box sx={{ display: 'flex', p: 2 }}>
                    <Box sx={{ mr: 2 }}>
                      <Avatar
                        src={product.image_url || undefined}
                        alt={product.name}
                        sx={{ width: 80, height: 80 }}
                        variant="rounded"
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {product.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {product.categories.join(', ')}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                        <Chip
                          label={product.status}
                          size="small"
                          color={
                            product.status === 'Active'
                              ? 'success'
                              : product.status === 'Draft'
                              ? 'warning'
                              : 'default'
                          }
                        />
                        <Chip
                          label={`$${product.price}`}
                          size="small"
                          color="primary"
                        />
                      </Stack>
                      {/* <Box sx={{ mt: 1 }}>
  {product.variants.map((v) => (
    <Box key={v.id}>
      {v.inStoreStock <= 2 && (
        <Chip size="small" color="warning" label="Low In-Store Stock" sx={{ mr: 1, mb: 0.5 }} />
      )}
      {v.onlineStock <= 2 && (
        <Chip size="small" color="warning" label="Low Online Stock" sx={{ mb: 0.5 }} />
      )}
    </Box>
  ))}
</Box> */}

                    </Box>
                    <Box>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenMenu(e, product.id);
                        }}
                      >
                        <MoreVert />
                      </IconButton>
                    </Box>
                  </Box>
                </CardActionArea>
                <Divider />
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
                  <Button
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteProduct(product.id);
                    }}
                  >
                    Delete
                  </Button>
                  <Checkbox
                    checked={selectedProducts.includes(product.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleSelectProduct(product.id);
                    }}
                  />
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        // Desktop Table View
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Checkbox
                    checked={selectedProducts.length === products.length}
                    indeterminate={
                      selectedProducts.length > 0 &&
                      selectedProducts.length < products.length
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Image</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Locations</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Inventory</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProducts.map((product) => (
                <TableRow
                  key={product.id}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'dark' ? '#333' : '#f5f5f5',
                    },
                  }}
                  onClick={() => navigate(`/products/edit/${product.id}`)}
                >
                  <TableCell>
                    <Checkbox
                      checked={selectedProducts.includes(product.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSelectProduct(product.id);
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <img
                      src={product.image_url || undefined}
                      alt={product.name}
                      style={{ width: 50, height: 50, objectFit: 'cover' }}
                    />
                  </TableCell>
                  <TableCell>{product.name}</TableCell>
                  <TableCell>{product.categories.join(', ')}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {product.variants.length} variants in{' '}
                      {
                        Array.from(
                          product.variants.flatMap((variant) =>
                            variant.locations.map((loc) => loc.name)
                          ).reduce((unique, loc) => unique.add(loc), new Set<string>())
                        ).length
                      }{' '}
                      location(s)
                      <IconButton
                        onClick={(event) => {
                          event.stopPropagation();
                          handleOpenMenu(event, product.id);
                        }}
                      >
                        <ExpandMore />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>{product.status}</TableCell>
                  <TableCell>
  <Box>
    {/* {product.variants.map((v) => (
      <Box key={v.id}>
        {v.inStoreStock <= 2 && (
          <Chip size="small" color="warning" label="Low In-Store Stock" sx={{ mr: 1, mb: 0.5 }} />
        )}
        {v.onlineStock <= 2 && (
          <Chip size="small" color="warning" label="Low Online Stock" sx={{ mb: 0.5 }} />
        )}
      </Box>
    ))} */}
    <Typography variant="body2">
      {product.inventoryDetails?.totalStock ?? 0} total units
    </Typography>
  </Box>
</TableCell>

                  <TableCell>${product.price}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteProduct(product.id);
                      }}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        {selectedProduct &&
          products
            .find((p) => p.id === selectedProduct)
            ?.variants.map((variant) => (
              <MenuItem key={variant.id}>
                {variant.sku} -{' '}
                {variant.locations
                  .map((loc) => `${loc.name} (${loc.stock})`)
                  .join(', ')}
              </MenuItem>
            ))}
      </Menu>
    </Box>
  );
};

export default ProductListPage;